import React, { useState, useEffect } from 'react';
import { workAssignmentAPI, userAPI, branchAPI, leaveAPI, holidayAPI } from '../services/api';
import styled from 'styled-components';
import { 
  FiBriefcase, 
  FiFileText, 
  FiCalendar, 
  FiShield, 
  FiAlertTriangle, 
  FiRotateCcw, 
  FiNavigation, 
  FiCheckCircle,
  FiInfo
} from './Icons';

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 900px;
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
`;

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
`;

const CheckboxContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
  background: #f9f9f9;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    background: #e9ecef;
  }
  
  &.disabled {
    opacity: 0.6;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
  }
  
  &.conflict {
    background: #fff5f5;
    border: 1px solid #fed7d7;
  }
  
  input[type="checkbox"] {
    margin-right: 8px;
    
    &:disabled {
      cursor: not-allowed;
    }
  }
  
  label {
    margin: 0;
    font-weight: normal;
    cursor: pointer;
    flex: 1;
    font-size: 14px;
    
    &.disabled {
      cursor: not-allowed;
      color: #6c757d;
    }
  }
`;

const ConflictInfo = styled.div`
  font-size: 12px;
  color: #dc3545;
  margin-top: 4px;
  font-style: italic;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
  grid-column: 1 / -1;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  
  &.primary {
    background-color: #007bff;
    color: white;
  }
  
  &.secondary {
    background-color: #6c757d;
    color: white;
  }
  
  &.success {
    background-color: #28a745;
    color: white;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const EmployeeSection = styled.div`
  grid-column: 1 / -1;
`;

const SummaryBox = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 4px;
  margin-top: 15px;
  grid-column: 1 / -1;
  border-left: 4px solid #007bff;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
`;

const EmployeeInfoSection = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const EmployeeInfoCard = styled.div`
  background: white;
  padding: 15px;
  margin-bottom: 15px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
`;

const EmployeeInfoHeader = styled.div`
  font-weight: bold;
  color: #495057;
  margin-bottom: 10px;
  font-size: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  border-bottom: 1px solid #f1f3f4;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-weight: 500;
  color: #6c757d;
`;

const InfoValue = styled.span`
  color: #495057;
`;

const LeaveDate = styled.span`
  background: #fff3cd;
  color: #856404;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  margin: 2px;
  display: inline-block;
`;

const HolidayDate = styled.span`
  background: #d1ecf1;
  color: #0c5460;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  margin: 2px;
  display: inline-block;
`;

// Custom Date Range Picker Component
const DateRangePickerContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
  background: white;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-top: 10px;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const CalendarNavButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #0056b3;
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const DayHeader = styled.div`
  text-align: center;
  font-weight: bold;
  color: #666;
  padding: 5px;
  font-size: 12px;
`;

const DayCell = styled.div`
  text-align: center;
  padding: 8px 4px;
  cursor: pointer;
  font-size: 14px;
  border-radius: 4px;
  min-height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &.other-month {
    color: #ccc;
  }
  
  &.start-date {
    background: #007bff;
    color: white;
  }
  
  &.end-date {
    background: #007bff;
    color: white;
  }
  
  &.in-range {
    background: #e3f2fd;
    color: #0066cc;
  }
  
  &.today {
    background: #f8f9fa;
    border: 2px solid #007bff;
  }
  
  &:hover:not(.other-month) {
    background: #e9ecef;
  }
`;

const DateRangeInfo = styled.div`
  margin-bottom: 10px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 14px;
  color: #495057;
`;

const DateRangePicker = ({ startDate, endDate, onChange, currentDate }) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    // Use the currentDate prop from BranchCalendar if available
    if (currentDate) {
      return new Date(currentDate);
    }
    if (startDate) {
      return new Date(startDate);
    }
    return new Date();
  });
  
  const [selectingStart, setSelectingStart] = useState(true);
  
  const handleDateClick = (date) => {
    // Use a simple, reliable date string format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    if (selectingStart || !startDate) {
      // First click or resetting - set start date
      onChange({ startDate: dateStr, endDate: '' });
      setSelectingStart(false);
    } else {
      // Second click - set end date
      const startDateObj = new Date(startDate);
      const clickedDateObj = new Date(dateStr);
      
      if (clickedDateObj >= startDateObj) {
        onChange({ startDate, endDate: dateStr });
      } else {
        // If end date is before start date, swap them
        onChange({ startDate: dateStr, endDate: startDate });
      }
      setSelectingStart(true);
    }
  };
  
  const resetSelection = () => {
    onChange({ startDate: '', endDate: '' });
    setSelectingStart(true);
  };
  
  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Get first day of the month and adjust for Sunday start
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOfWeek = new Date(firstDay);
    startOfWeek.setDate(firstDay.getDate() - firstDay.getDay());
    
    const endOfWeek = new Date(lastDay);
    endOfWeek.setDate(lastDay.getDate() + (6 - lastDay.getDay()));
    
    const days = [];
    const currentDay = new Date(startOfWeek);
    
    while (currentDay <= endOfWeek) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    const today = new Date();
    const startDateObj = startDate ? new Date(startDate) : null;
    const endDateObj = endDate ? new Date(endDate) : null;
    
    return days.map((day, index) => {
      const isCurrentMonth = day.getMonth() === month;
      const isToday = day.toDateString() === today.toDateString();
      
      // Use simple date comparison
      const dayStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
      const isStartDate = startDate === dayStr;
      const isEndDate = endDate === dayStr;
      const isInRange = startDate && endDate && dayStr >= startDate && dayStr <= endDate;
      
      let className = '';
      if (!isCurrentMonth) className += 'other-month ';
      if (isToday) className += 'today ';
      if (isStartDate) className += 'start-date ';
      if (isEndDate) className += 'end-date ';
      if (isInRange && !isStartDate && !isEndDate) className += 'in-range ';
      
      return (
        <DayCell
          key={index}
          className={className.trim()}
          onClick={() => isCurrentMonth && handleDateClick(day)}
        >
          {day.getDate()}
        </DayCell>
      );
    });
  };
  
  const formatDateRange = () => {
    if (startDate && endDate) {
      // Use simple date parsing and create a custom display format
      const [startYear, startMonth, startDay] = startDate.split('-');
      const [endYear, endMonth, endDay] = endDate.split('-');
      
      const startDateObj = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));
      const endDateObj = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));
      
      const days = Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24)) + 1;
      
      // Create simple display format
      const startDisplay = `${startMonth}/${startDay}/${startYear}`;
      const endDisplay = `${endMonth}/${endDay}/${endYear}`;
      
      return `${startDisplay} - ${endDisplay} (${days} days)`;
    } else if (startDate) {
      const [year, month, day] = startDate.split('-');
      return `Start: ${month}/${day}/${year} (select end date)`;
    }
    return 'Click to select start date, then click again for end date';
  };
  
  return (
    <DateRangePickerContainer>
      <DateRangeInfo>
        {formatDateRange()}
        {(startDate || endDate) && (
          <button 
            onClick={resetSelection}
            style={{ 
              marginLeft: '10px', 
              padding: '2px 8px', 
              background: '#dc3545', 
              color: 'white', 
              border: 'none', 
              borderRadius: '3px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Reset
          </button>
        )}
      </DateRangeInfo>
      
      <CalendarHeader>
        <CalendarNavButton 
          onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}
        >
          ‹
        </CalendarNavButton>
        <span style={{ fontWeight: 'bold' }}>
          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <CalendarNavButton 
          onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}
        >
          ›
        </CalendarNavButton>
      </CalendarHeader>
      
      <CalendarGrid>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <DayHeader key={day}>{day}</DayHeader>
        ))}
        {renderCalendar()}
      </CalendarGrid>
    </DateRangePickerContainer>
  );
};

const BulkAssignmentForm = ({ isOpen, onClose, defaultBranchId, currentDate, onSuccess }) => {
  // Global settings (shared across all assignment sets)
  const [globalSettings, setGlobalSettings] = useState({
    role: '',
    selectedEmployees: [],
    branchId: defaultBranchId || ''
  });
  
  // Assignment sets (only date ranges and shift types)
  const [assignmentSets, setAssignmentSets] = useState([{
    id: 1,
    startDate: '',
    endDate: '',
    shiftType: 'day'
  }]);
  
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [selectedEmployeesInfo, setSelectedEmployeesInfo] = useState({});

  // Helper function to get the start and end dates of the current month
  const getCurrentMonthDateRange = () => {
    if (!currentDate) {
      return { startDate: '', endDate: '' };
    }
    
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    // First day of the month
    const startDate = new Date(year, month, 1);
    // Last day of the month
    const endDate = new Date(year, month + 1, 0);
    
    // Format as YYYY-MM-DD
    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };
    
    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      fetchBranches();
      fetchAllLeaveAndHolidayData(); // Fetch leave/holiday data immediately
      
      // Reset global settings and assignment sets - don't auto-fill dates
      setGlobalSettings({
        role: '',
        selectedEmployees: [],
        branchId: defaultBranchId || ''
      });
      setAssignmentSets([{
        id: 1,
        startDate: '',
        endDate: '',
        shiftType: 'day'
      }]);
      setError('');
    }
  }, [isOpen, defaultBranchId, currentDate]); // Added currentDate dependency

  // Fetch conflict data when any assignment set's date range changes
  useEffect(() => {
    if (isOpen) {
      const hasDateRanges = assignmentSets.some(set => set.startDate && set.endDate);
      if (hasDateRanges) {
        fetchConflictData();
      }
    }
  }, [isOpen, assignmentSets]);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setError('Failed to fetch users');
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll();
      setBranches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
  };

  const fetchConflictData = async () => {
    // Get all unique date ranges from assignment sets
    const dateRanges = assignmentSets
      .filter(set => set.startDate && set.endDate)
      .map(set => ({ startDate: set.startDate, endDate: set.endDate }));
    
    if (dateRanges.length === 0) return;

    try {
      // Find the overall min and max dates to fetch all needed data at once
      const allStartDates = dateRanges.map(range => new Date(range.startDate));
      const allEndDates = dateRanges.map(range => new Date(range.endDate));
      const minDate = new Date(Math.min(...allStartDates));
      const maxDate = new Date(Math.max(...allEndDates));
      
      const minDateStr = minDate.toISOString().split('T')[0];
      const maxDateStr = maxDate.toISOString().split('T')[0];
      
      // Fetch holidays for the overall date range
      const holidaysResponse = await holidayAPI.getAll({
        start_date: minDateStr,
        end_date: maxDateStr
      });
      
      // Fetch leave requests for the overall date range
      const leaveResponse = await leaveAPI.getLeaveRequests({
        start_date: minDateStr,
        end_date: maxDateStr,
        status: 'approved'
      });
      
      // Fetch existing work assignments for the overall date range
      const assignmentsResponse = await workAssignmentAPI.getAll({
        start_date: minDateStr,
        end_date: maxDateStr
      });
      
      setHolidays(holidaysResponse.data || []);
      setLeaveRequests(leaveResponse.data || []);
      setExistingAssignments(assignmentsResponse.data || []);
      
    } catch (error) {
      console.error('Error fetching conflict data:', error);
    }
  };

  // Fetch all leave and holiday data for the selected month
  const fetchAllLeaveAndHolidayData = async () => {
    try {
      // Get selected month range from currentDate prop, fallback to current month
      const baseDate = currentDate || new Date();
      const selectedMonthStart = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
      const selectedMonthEnd = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);
      
      const startDateStr = selectedMonthStart.toISOString().split('T')[0];
      const endDateStr = selectedMonthEnd.toISOString().split('T')[0];
      
      // Fetch all holidays (not filtered by date for now, we'll filter in frontend)
      const holidaysResponse = await holidayAPI.getAll();
      
      // Fetch all leave requests (not filtered by date for now, we'll filter in frontend)
      const leaveResponse = await leaveAPI.getLeaveRequests();
      
      setHolidays(holidaysResponse.data || []);
      setLeaveRequests(leaveResponse.data || []);
      
    } catch (error) {
      console.error('Error fetching leave/holiday data:', error);
    }
  };

  // Function to get detailed leave and holiday info for selected employees
  const getSelectedEmployeesInfo = () => {
    const info = {};
    
    // Use the selected month from currentDate prop, fallback to current month
    const baseDate = currentDate || new Date();
    const currentMonthStart = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    const currentMonthEnd = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0); // Last day of selected month
    
    globalSettings.selectedEmployees.forEach(employeeId => {
      const employee = users.find(u => u.id === employeeId);
      if (!employee) return;
      
      // Get ALL employee's leave requests within current month
      const employeeLeaves = leaveRequests.filter(leave => {
        if (leave.user_id !== employeeId) return false;
        const leaveDate = new Date(leave.leave_date);
        return leaveDate >= currentMonthStart && leaveDate <= currentMonthEnd;
      }).sort((a, b) => new Date(a.leave_date) - new Date(b.leave_date));
      
      // Get ALL holidays within current month
      const employeeHolidays = holidays.filter(holiday => {
        // holiday.date is the canonical field used across the app
        const holidayDate = new Date(holiday.date || holiday.holiday_date);
        const inRange = holidayDate >= currentMonthStart && holidayDate <= currentMonthEnd;

        if (!inRange) return false;

        // For now, include all holidays (can be refined based on holiday group logic later)
        return true;
      }).sort((a, b) => new Date(a.date || a.holiday_date) - new Date(b.date || b.holiday_date));
      
      info[employeeId] = {
        employee,
        leaves: employeeLeaves,
        holidays: employeeHolidays,
        timeframe: {
          start: currentMonthStart,
          end: currentMonthEnd
        }
      };
    });
    
    return info;
  };

  // Update selected employees info when selection changes
  useEffect(() => {
    if (globalSettings.selectedEmployees.length > 0) {
      setSelectedEmployeesInfo(getSelectedEmployeesInfo());
    } else {
      setSelectedEmployeesInfo({});
    }
  }, [globalSettings.selectedEmployees, leaveRequests, holidays, users]);

  // Functions for managing multiple assignment sets
  const addAssignmentSet = () => {
    const newId = Math.max(...assignmentSets.map(set => set.id)) + 1;
    setAssignmentSets(prev => [...prev, {
      id: newId,
      startDate: '',
      endDate: '',
      shiftType: 'day'
    }]);
  };

  const removeAssignmentSet = (id) => {
    if (assignmentSets.length > 1) {
      setAssignmentSets(prev => prev.filter(set => set.id !== id));
    }
  };

  const updateAssignmentSet = (id, updates) => {
    setAssignmentSets(prev => prev.map(set => 
      set.id === id ? { ...set, ...updates } : set
    ));
  };

  // Check if user has conflicts for the selected date range
  const getUserConflicts = (user, assignmentSet) => {
    if (!assignmentSet.startDate || !assignmentSet.endDate) return null;

    const conflicts = [];
    const holidayWarnings = [];
    const startDate = new Date(assignmentSet.startDate);
    const endDate = new Date(assignmentSet.endDate);

    // Check each date in the range
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      
      // Check for leave conflicts
      const leaveConflict = leaveRequests.find(leave => 
        leave.user_id === user.id && leave.leave_date === dateStr
      );
      
      if (leaveConflict) {
        conflicts.push({
          date: dateStr,
          type: 'leave',
          description: `On ${leaveConflict.leave_type?.name || 'leave'}`
        });
      }
      
      // Check for holiday warnings (not blocking, just warning)
      const holidayConflict = holidays.find(holiday => 
        holiday.date === dateStr && 
        holiday.holiday_group_id === user.holiday_group_id
      );
      
      if (holidayConflict) {
        holidayWarnings.push({
          date: dateStr,
          type: 'holiday',
          description: `Holiday: ${holidayConflict.name} (Group ${user.holiday_group?.name || 'Unknown'}) - Overtime`
        });
      }
      
      // Check for existing assignment conflicts (assigned to different branch on same date/shift)
      const assignmentConflict = existingAssignments.find(assignment => 
        assignment.user_id === user.id && 
        assignment.assignment_date === dateStr && 
        assignment.shift_type === assignmentSet.shiftType &&
        assignment.branch_id !== parseInt(assignmentSet.branchId)
      );
      
      if (assignmentConflict) {
        conflicts.push({
          date: dateStr,
          type: 'assignment',
          description: `Already assigned to ${assignmentConflict.branch?.name || 'another branch'}`
        });
      }
    }
    
    // Return both blocking conflicts and holiday warnings
    return {
      conflicts: conflicts.length > 0 ? conflicts : null,
      holidayWarnings: holidayWarnings.length > 0 ? holidayWarnings : null
    };
  };

  const handleRoleChange = (role) => {
    setGlobalSettings(prev => ({ 
      ...prev, 
      role,
      selectedEmployees: [] // Clear selected employees when role changes
    }));
  };

  const handleEmployeeToggle = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    
    // Check conflicts across all assignment sets for this user
    let hasBlockingConflicts = false;
    let hasHolidayWarnings = false;
    
    for (const set of assignmentSets) {
      if (set.startDate && set.endDate) {
        const conflictResult = getUserConflicts(user, { ...globalSettings, ...set });
        if (conflictResult?.conflicts && conflictResult.conflicts.length > 0) {
          hasBlockingConflicts = true;
          break;
        }
        if (conflictResult?.holidayWarnings && conflictResult.holidayWarnings.length > 0) {
          hasHolidayWarnings = true;
        }
      }
    }
    
    if (hasBlockingConflicts) {
      // Don't allow selection of users with blocking conflicts (leave, existing assignments)
      return;
    }
    
    // Show warning for holiday overtime but allow selection
    if (hasHolidayWarnings && !globalSettings.selectedEmployees.includes(userId)) {
      const confirmOT = window.confirm(
        `⚠️ HOLIDAY OVERTIME WARNING\n\n` +
        `Employee: ${user.name}\n\n` +
        `This employee has public holidays during the selected period.\n` +
        `Assignments on holidays will be considered overtime work.\n\n` +
        `Do you want to include this employee for overtime assignments?`
      );
      
      if (!confirmOT) {
        return;
      }
    }

    const isSelected = globalSettings.selectedEmployees.includes(userId);
    const newSelectedEmployees = isSelected
      ? globalSettings.selectedEmployees.filter(id => id !== userId)
      : [...globalSettings.selectedEmployees, userId];
    
    setGlobalSettings(prev => ({ ...prev, selectedEmployees: newSelectedEmployees }));
  };

  const handleSelectAll = () => {
    const filteredUsers = getFilteredUsers();
    // Only include users without blocking conflicts across all assignment sets
    const availableUsers = filteredUsers.filter(user => {
      for (const set of assignmentSets) {
        if (set.startDate && set.endDate) {
          const conflictResult = getUserConflicts(user, { ...globalSettings, ...set });
          // Only block for actual conflicts, not holiday warnings
          if (conflictResult?.conflicts && conflictResult.conflicts.length > 0) {
            return false;
          }
        }
      }
      return true;
    });
    
    const availableUserIds = availableUsers.map(user => user.id);
    const selectedAvailableIds = globalSettings.selectedEmployees.filter(id => availableUserIds.includes(id));
    
    const newSelectedEmployees = selectedAvailableIds.length === availableUserIds.length ? [] : availableUserIds;
    setGlobalSettings(prev => ({ ...prev, selectedEmployees: newSelectedEmployees }));
  };

  // Map simplified role categories to actual roles (base + supervisor)
  const getRolesForCategory = (category) => {
    const roleMap = {
      'manager': ['store_manager', 'manager_assistant'],
      'makeup': ['makeup', 'makeup_supervisor'],
      'photographer': ['photographer', 'photographer_supervisor'],
      'retoucher': ['retoucher', 'retoucher_supervisor']
    };
    return roleMap[category] || [category];
  };

  const getFilteredUsers = () => {
    if (!globalSettings.role) return [];
    
    const allowedRoles = getRolesForCategory(globalSettings.role);
    
    const filtered = users.filter(user => {
      // Ensure both role values are strings and normalize case
      const userRole = String(user.role || '').toLowerCase().trim();
      
      // Filter by role category (includes base + supervisor roles)
      if (!allowedRoles.includes(userRole)) return false;
      
      // Filter by search term
      if (employeeSearchTerm.trim()) {
        const searchTerm = employeeSearchTerm.toLowerCase().trim();
        const firstName = String(user.first_name || '').toLowerCase();
        const lastName = String(user.last_name || '').toLowerCase();
        const fullName = `${firstName} ${lastName}`.toLowerCase();
        const email = String(user.email || '').toLowerCase();
        const employeeId = String(user.employee_id || '').toLowerCase();
        const name = String(user.name || '').toLowerCase(); // In case name is used instead of first_name/last_name
        
        return fullName.includes(searchTerm) || 
               name.includes(searchTerm) ||
               firstName.includes(searchTerm) ||
               lastName.includes(searchTerm) ||
               email.includes(searchTerm) || 
               employeeId.includes(searchTerm);
      }
      
      return true;
    });

    // Sort employees by priority: branch match > moveable > non-moveable
    const selectedBranchId = parseInt(globalSettings.branchId);
    
    const sorted = filtered.sort((a, b) => {
      const aHomeBranchId = a.home_branch_id || a.home_branch?.id;
      const bHomeBranchId = b.home_branch_id || b.home_branch?.id;
      
      // Priority 1: Employees from the selected branch (highest priority)
      const aIsFromSelectedBranch = selectedBranchId && aHomeBranchId === selectedBranchId;
      const bIsFromSelectedBranch = selectedBranchId && bHomeBranchId === selectedBranchId;
      
      if (aIsFromSelectedBranch && !bIsFromSelectedBranch) return -1;
      if (!aIsFromSelectedBranch && bIsFromSelectedBranch) return 1;
      
      // If both are from selected branch OR both are not from selected branch, 
      // then sort by moveable status
      if (aIsFromSelectedBranch === bIsFromSelectedBranch) {
        // Priority 2: Moveable employees (higher priority than non-moveable)
        const aIsMoveable = a.is_moveable;
        const bIsMoveable = b.is_moveable;
        
        if (aIsMoveable && !bIsMoveable) return -1;
        if (!aIsMoveable && bIsMoveable) return 1;
        
        // If both have same moveable status, sort alphabetically by name
        const aName = `${a.first_name} ${a.last_name}`.toLowerCase();
        const bName = `${b.first_name} ${b.last_name}`.toLowerCase();
        return aName.localeCompare(bName);
      }
      
      return 0;
    });

    return sorted;
  };

  const validateAssignmentSet = (set) => {
    if (!set.startDate) return 'Please select a start date';
    if (!set.endDate) return 'Please select an end date';
    if (new Date(set.startDate) > new Date(set.endDate)) return 'End date must be after start date';
    return '';
  };

  const validateForm = () => {
    // Validate global settings
    if (!globalSettings.role) return 'Please select a role';
    if (globalSettings.selectedEmployees.length === 0) return 'Please select at least one employee';
    if (!globalSettings.branchId) return 'Please select a branch';
    
    // Validate all assignment sets
    for (let i = 0; i < assignmentSets.length; i++) {
      const set = assignmentSets[i];
      const error = validateAssignmentSet(set);
      if (error) {
        return `Assignment Set ${i + 1}: ${error}`;
      }
    }
    
    // Check for conflicts between assignment sets (same user, date, shift)
    const assignmentKeys = new Set();
    const userDateAssignments = {}; // Track all dates per user for consecutive day validation
    
    for (let setIndex = 0; setIndex < assignmentSets.length; setIndex++) {
      const set = assignmentSets[setIndex];
      if (!set.startDate || !set.endDate || globalSettings.selectedEmployees.length === 0) continue;
      
      const startDate = new Date(set.startDate);
      const endDate = new Date(set.endDate);
      
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        for (const userId of globalSettings.selectedEmployees) {
          const assignmentDate = d.toISOString().split('T')[0];
          const assignmentKey = `${userId}-${assignmentDate}-${set.shiftType}`;
          
          // Check for duplicate assignments (same user, date, shift)
          if (assignmentKeys.has(assignmentKey)) {
            const user = users.find(u => u.id === userId);
            const userName = user ? `${user.first_name} ${user.last_name}` : `User ${userId}`;
            return `Conflict detected: ${userName} is assigned multiple roles on ${assignmentDate} (${set.shiftType} shift). Each employee can only have one assignment per date/shift.`;
          }
          
          assignmentKeys.add(assignmentKey);
          
          // Track dates for consecutive day validation
          if (!userDateAssignments[userId]) {
            userDateAssignments[userId] = [];
          }
          userDateAssignments[userId].push(new Date(assignmentDate));
        }
      }
    }
    
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const allAssignments = [];
      const assignmentKeys = new Set(); // To track duplicates

      // Process each assignment set
      for (const set of assignmentSets) {
        const startDate = new Date(set.startDate);
        const endDate = new Date(set.endDate);

        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          for (const userId of globalSettings.selectedEmployees) {
            const assignmentDate = d.toISOString().split('T')[0];
            const assignmentKey = `${userId}-${assignmentDate}-${set.shiftType}`;
            
            // Check for duplicate assignments (same user, date, shift)
            if (assignmentKeys.has(assignmentKey)) {
              console.warn(`Skipping duplicate assignment: User ${userId} on ${assignmentDate} ${set.shiftType} shift`);
              continue;
            }
            
            // Get the actual user role (not the category)
            const user = users.find(u => u.id === userId);
            if (!user) {
              console.error(`User ${userId} not found`);
              continue;
            }
            
            assignmentKeys.add(assignmentKey);
            allAssignments.push({
              user_id: userId,
              branch_id: parseInt(globalSettings.branchId),
              assignment_date: assignmentDate,
              shift_type: set.shiftType,
              role: user.role  // Use actual user role, not category
            });
          }
        }
      }

      const response = await workAssignmentAPI.createBulk(allAssignments);

      if (onSuccess) {
        try {
          await onSuccess();
        } catch (callbackError) {
          console.error('Error in onSuccess callback:', callbackError);
          // Don't throw the error - assignments were created successfully
        }
      }

      reset();

      onClose();
    } catch (error) {
      console.error('Error creating bulk assignments:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Error creating assignments';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Handle new structured error format with message and errors
        if (typeof detail === 'object' && detail.message && detail.errors) {
          errorMessage = `${detail.message}\n\nDetails:\n${detail.errors.join('\n')}`;
        }
        // Handle legacy object with errors array only
        else if (typeof detail === 'object' && detail.errors && Array.isArray(detail.errors)) {
          errorMessage = `Error creating assignments:\n${detail.errors.join('\n')}`;
        } 
        // Handle simple string detail
        else if (typeof detail === 'string') {
          errorMessage = `Error creating assignments: ${detail}`;
        } 
        // Handle unknown object format
        else {
          errorMessage = `Error creating assignments: ${JSON.stringify(detail)}`;
        }
      } else if (error.message) {
        errorMessage = `Error creating assignments: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setGlobalSettings({
      role: '',
      selectedEmployees: [],
      branchId: defaultBranchId || ''
    });
    setAssignmentSets([{
      id: 1,
      startDate: '',
      endDate: '',
      shiftType: 'day'
    }]);
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  const filteredUsers = getFilteredUsers();

  return (
    <Modal onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <ModalContent>
        <h2>Bulk Create Assignments</h2>
        
        <form onSubmit={handleSubmit}>
          {/* Global Settings Section */}
          <div style={{ 
            border: '2px solid #007bff', 
            borderRadius: '8px', 
            padding: '20px', 
            marginBottom: '20px',
            backgroundColor: '#f8f9fa'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#007bff' }}>
              Assignment Details (Applied to All Sets)
            </h3>
            
            <FormContainer>
              <div>
                <FormSection>
                  <Label>Branch *</Label>
                  <Select 
                    value={globalSettings.branchId} 
                    onChange={(e) => setGlobalSettings(prev => ({ ...prev, branchId: e.target.value }))}
                    required
                  >
                    <option value="">Select branch...</option>
                    {branches.map(branch => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} - {branch.location}
                      </option>
                    ))}
                  </Select>
                </FormSection>

                <FormSection>
                  <Label>Role *</Label>
                  <Select 
                    value={globalSettings.role} 
                    onChange={(e) => handleRoleChange(e.target.value)}
                    required
                  >
                    <option value="">Choose a role...</option>
                    <option value="manager">Manager</option>
                    <option value="makeup">Makeup</option>
                    <option value="photographer">Photographer</option>
                    <option value="retoucher">Retoucher</option>
                  </Select>
                </FormSection>
              </div>

              <EmployeeSection>
                <FormSection>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <Label style={{ margin: 0 }}>
                      Select Employees {globalSettings.role && `(${globalSettings.role})`}
                      {globalSettings.selectedEmployees.length > 0 && ` - ${globalSettings.selectedEmployees.length} selected`}
                    </Label>
                    {filteredUsers.length > 0 && (
                      <Button 
                        type="button" 
                        className="secondary" 
                        onClick={handleSelectAll}
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        {(() => {
                          const availableUsers = filteredUsers.filter(user => {
                            // Check conflicts across all assignment sets
                            for (const set of assignmentSets) {
                              if (set.startDate && set.endDate) {
                                const conflictResult = getUserConflicts(user, { ...globalSettings, ...set });
                                // Only block for actual conflicts, not holiday warnings
                                if (conflictResult?.conflicts && conflictResult.conflicts.length > 0) {
                                  return false;
                                }
                              }
                            }
                            return true;
                          });
                          const selectedAvailableIds = globalSettings.selectedEmployees.filter(id => 
                            availableUsers.some(user => user.id === id)
                          );
                          return selectedAvailableIds.length === availableUsers.length ? 'Deselect All' : 'Select All Available';
                        })()}
                      </Button>
                    )}
                  </div>
                  
                  {!globalSettings.role ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                      Please select a branch and role first to see available employees
                    </div>
                  ) : (
                    <>
                      {/* Search Input - Always show when role is selected */}
                      <div style={{ marginBottom: '15px' }}>
                        <Label style={{ fontSize: '14px', marginBottom: '5px', display: 'block' }}>
                          Search Employees
                        </Label>
                        <input
                          type="text"
                          value={employeeSearchTerm}
                          onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                          placeholder="Search by name, email, or employee ID..."
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #e1e5e9',
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: '#fff'
                          }}
                        />
                      </div>

                      {filteredUsers.length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
                          {employeeSearchTerm.trim() ? 
                            `No employees found matching "${employeeSearchTerm}" for role: ${globalSettings.role}` :
                            `No employees found for role: ${globalSettings.role}`
                          }
                          <br />
                          <small>Total users loaded: {users.length}</small>
                        </div>
                      ) : (
                        <>
                          {/* Color Legend */}
                      <div style={{ 
                        marginBottom: '15px', 
                        padding: '12px', 
                        backgroundColor: '#f8f9fa', 
                        border: '1px solid #e9ecef', 
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>
                          🎯 Employee Priority Legend:
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <div style={{ 
                              width: '12px', 
                              height: '12px', 
                              backgroundColor: '#d4edda', 
                              border: '1px solid #c3e6cb',
                              borderRadius: '3px' 
                            }}></div>
                            <span style={{ color: '#155724' }}>Home Branch (Best Choice)</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <div style={{ 
                              width: '12px', 
                              height: '12px', 
                              backgroundColor: '#fff3cd', 
                              border: '1px solid #ffeaa7',
                              borderRadius: '3px' 
                            }}></div>
                            <span style={{ color: '#856404' }}>Moveable (Good Option)</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <div style={{ 
                              width: '12px', 
                              height: '12px', 
                              backgroundColor: '#f8d7da', 
                              border: '1px solid #f5c6cb',
                              borderRadius: '3px' 
                            }}></div>
                            <span style={{ color: '#721c24' }}>Non-Moveable (Use with Caution)</span>
                          </div>
                        </div>
                      </div>
                      
                      <CheckboxContainer>
                      {filteredUsers.map(user => {
                        const homeBranchName = user.home_branch?.name || 
                                              user.home_branch_name || 
                                              (user.home_branch_id && `Branch ${user.home_branch_id}`) || 
                                              'Unknown';
                        const moveableText = user.is_moveable ? 'Moveable' : 'Not Moveable';
                        
                        // Determine employee priority and color
                        const selectedBranchId = parseInt(globalSettings.branchId);
                        const userHomeBranchId = user.home_branch_id || user.home_branch?.id;
                        const isFromSelectedBranch = selectedBranchId && userHomeBranchId === selectedBranchId;
                        
                        let priorityColor, priorityBorder, priorityText;
                        if (isFromSelectedBranch) {
                          // Green - Home branch (best choice)
                          priorityColor = '#d4edda';
                          priorityBorder = '#c3e6cb';
                          priorityText = '#155724';
                        } else if (user.is_moveable) {
                          // Yellow - Moveable (good option)
                          priorityColor = '#fff3cd';
                          priorityBorder = '#ffeaa7';
                          priorityText = '#856404';
                        } else {
                          // Red - Non-moveable (use with caution)
                          priorityColor = '#f8d7da';
                          priorityBorder = '#f5c6cb';
                          priorityText = '#721c24';
                        }
                        
                        // Check conflicts across all assignment sets
                        let blockingConflicts = null;
                        let holidayWarnings = null;
                        
                        for (const set of assignmentSets) {
                          if (set.startDate && set.endDate) {
                            const conflictResult = getUserConflicts(user, { ...globalSettings, ...set });
                            if (conflictResult?.conflicts && conflictResult.conflicts.length > 0) {
                              blockingConflicts = conflictResult.conflicts;
                              break;
                            }
                            if (conflictResult?.holidayWarnings && conflictResult.holidayWarnings.length > 0) {
                              holidayWarnings = conflictResult.holidayWarnings;
                            }
                          }
                        }
                        
                        const hasBlockingConflicts = blockingConflicts && blockingConflicts.length > 0;
                        const hasHolidayWarnings = holidayWarnings && holidayWarnings.length > 0;
                        
                        return (
                          <CheckboxItem 
                            key={user.id} 
                            className={hasBlockingConflicts ? 'conflict' : ''}
                            style={{
                              backgroundColor: priorityColor,
                              border: `1px solid ${priorityBorder}`,
                              color: priorityText
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                  type="checkbox"
                                  id={`user-${user.id}`}
                                  checked={globalSettings.selectedEmployees.includes(user.id)}
                                  onChange={() => handleEmployeeToggle(user.id)}
                                  disabled={hasBlockingConflicts}
                                />
                                <label 
                                  htmlFor={`user-${user.id}`}
                                  className={hasBlockingConflicts ? 'disabled' : ''}
                                  style={{ marginLeft: '8px', flex: 1 }}
                                >
                                  {isFromSelectedBranch && <FiBriefcase style={{ marginRight: '4px' }} />}
                                  {!isFromSelectedBranch && user.is_moveable && <FiRotateCcw style={{ marginRight: '4px' }} />}
                                  {!isFromSelectedBranch && !user.is_moveable && <FiNavigation style={{ marginRight: '4px' }} />}
                                  {user.name} ({homeBranchName} - {moveableText})
                                  {hasBlockingConflicts && <FiAlertTriangle style={{ color: '#dc3545', marginLeft: '5px' }} />}
                                  {hasHolidayWarnings && !hasBlockingConflicts && <span style={{ color: '#ff9800', marginLeft: '5px' }}>⚠️ OT</span>}
                                </label>
                              </div>
                              {hasBlockingConflicts && (
                                <ConflictInfo>
                                  Conflicts: {blockingConflicts.slice(0, 2).map(c => c.description || c).join(', ')}
                                  {blockingConflicts.length > 2 && ` +${blockingConflicts.length - 2} more`}
                                </ConflictInfo>
                              )}
                              {hasHolidayWarnings && !hasBlockingConflicts && (
                                <ConflictInfo style={{ color: '#ff9800', backgroundColor: '#fff3cd', border: '1px solid #ffeaa7' }}>
                                  Holiday Overtime: {holidayWarnings.slice(0, 2).map(h => h.description || h).join(', ')}
                                  {holidayWarnings.length > 2 && ` +${holidayWarnings.length - 2} more`}
                                </ConflictInfo>
                              )}
                            </div>
                          </CheckboxItem>
                        );
                      })}
                    </CheckboxContainer>
                        </>
                      )}
                    </>
                  )}
                </FormSection>
              </EmployeeSection>
            </FormContainer>
          </div>

          {/* Selected Employees Information Section */}
          {globalSettings.selectedEmployees.length > 0 && Object.keys(selectedEmployeesInfo).length > 0 && (
            <EmployeeInfoSection>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#495057' }}>
                <FiFileText style={{ marginRight: '8px' }} />
                Selected Employees Information
                {currentDate && (
                  <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#6c757d', marginLeft: '8px' }}>
                    ({currentDate.toLocaleDateString('en', { month: 'long', year: 'numeric' })})
                  </span>
                )}
              </h3>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {globalSettings.selectedEmployees.map(employeeId => {
                  const info = selectedEmployeesInfo[employeeId];
                  if (!info) return null;
                  
                  const { employee, leaves, holidays, timeframe } = info;
                  
                  return (
                    <EmployeeInfoCard key={employeeId}>
                      <EmployeeInfoHeader>
                        {employee.name} ({employee.role})
                        <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#6c757d', marginLeft: '10px' }}>
                          <FiCalendar style={{ marginRight: '4px' }} />Selected Month ({timeframe.start.toLocaleDateString('en', { month: 'long', year: 'numeric' })})
                        </span>
                      </EmployeeInfoHeader>
                      
                      <InfoRow>
                        <InfoLabel><FiShield style={{ marginRight: '4px' }} />Leave Requests:</InfoLabel>
                        <InfoValue>
                          {leaves.length === 0 ? (
                            <span style={{ color: '#28a745' }}><FiCheckCircle style={{ marginRight: '4px' }} />No leaves in this period</span>
                          ) : (
                            <div>
                              <div style={{ marginBottom: '5px', fontSize: '12px', color: '#495057' }}>
                                Total: {leaves.length} leave day{leaves.length > 1 ? 's' : ''}
                              </div>
                              {leaves.map(leave => (
                                <LeaveDate key={leave.id}>
                                  {new Date(leave.leave_date).toLocaleDateString()}
                                  {leave.leave_type && ` - ${leave.leave_type?.name || leave.leave_type}`}
                                </LeaveDate>
                              ))}
                            </div>
                          )}
                        </InfoValue>
                      </InfoRow>
                      
                      <InfoRow>
                        <InfoLabel>🎊 Holiday Dates:</InfoLabel>
                        <InfoValue>
                          {holidays.length === 0 ? (
                            <span style={{ color: '#6c757d' }}>No holidays in this period</span>
                          ) : (
                            <div>
                              <div style={{ marginBottom: '5px', fontSize: '12px', color: '#495057' }}>
                                Total: {holidays.length} holiday{holidays.length > 1 ? 's' : ''}
                              </div>
                              {holidays.map(holiday => (
                                <HolidayDate key={holiday.id}>
                                  {new Date(holiday.date || holiday.holiday_date).toLocaleDateString()} - {holiday.name}
                                </HolidayDate>
                              ))}
                            </div>
                          )}
                        </InfoValue>
                      </InfoRow>
                      
                      {(leaves.length > 0 || holidays.length > 0) && (
                        <InfoRow style={{ backgroundColor: '#fff3cd', padding: '8px', borderRadius: '4px', marginTop: '8px' }}>
                          <InfoLabel style={{ color: '#856404' }}><FiAlertTriangle style={{ marginRight: '4px' }} />Unavailable Days:</InfoLabel>
                          <InfoValue style={{ color: '#856404', fontWeight: 'bold' }}>
                            {leaves.length + holidays.length} total days to avoid
                          </InfoValue>
                        </InfoRow>
                      )}
                    </EmployeeInfoCard>
                  );
                })}
              </div>
              <div style={{ marginTop: '15px', fontSize: '12px', color: '#6c757d', lineHeight: '1.4' }}>
                <FiFileText style={{ marginRight: '4px' }} /><strong>Selected Month Overview:</strong> Shows all leave requests and holidays for the selected calendar month only.
                <br/>
                🎯 <strong>Conflict Prevention:</strong> Use this information to avoid assigning work on unavailable dates.
                <br/>
                <FiFileText style={{ marginRight: '4px' }} /><strong>Real-time Data:</strong> Information updates automatically when you add/remove employees.
              </div>
            </EmployeeInfoSection>
          )}

          {/* Assignment Sets Section */}
          <h3 style={{ margin: '20px 0 16px 0', fontSize: '18px' }}>
            Date Ranges & Shift Types
          </h3>
          
          {assignmentSets.map((assignmentSet, index) => (
            <div key={assignmentSet.id} style={{ 
              border: '1px solid #e0e0e0', 
              borderRadius: '8px', 
              padding: '20px', 
              marginBottom: '16px',
              backgroundColor: '#fafafa'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '16px' 
              }}>
                <h4 style={{ margin: 0, fontSize: '16px' }}>
                  Assignment Set {index + 1}
                </h4>
                {assignmentSets.length > 1 && (
                  <Button 
                    type="button" 
                    onClick={() => removeAssignmentSet(assignmentSet.id)}
                    style={{ 
                      backgroundColor: '#dc3545',
                      padding: '4px 8px',
                      fontSize: '12px',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>

              <FormContainer>
                <div>
                  <FormSection>
                    <Label>Date Range *</Label>
                    <DateRangePicker
                      startDate={assignmentSet.startDate}
                      endDate={assignmentSet.endDate}
                      currentDate={currentDate}
                      onChange={({ startDate, endDate }) => updateAssignmentSet(assignmentSet.id, { startDate, endDate })}
                    />
                    {assignmentSet.startDate && assignmentSet.endDate && (() => {
                      // Use simple date parsing without timezone complications
                      const [startYear, startMonth, startDay] = assignmentSet.startDate.split('-');
                      const [endYear, endMonth, endDay] = assignmentSet.endDate.split('-');
                      
                      const startDateObj = new Date(parseInt(startYear), parseInt(startMonth) - 1, parseInt(startDay));
                      const endDateObj = new Date(parseInt(endYear), parseInt(endMonth) - 1, parseInt(endDay));
                      
                      const daysDifference = Math.ceil((endDateObj - startDateObj) / (1000 * 60 * 60 * 24)) + 1;
                      
                      // Simple display format
                      const startDisplay = `${startMonth}/${startDay}/${startYear}`;
                      const endDisplay = `${endMonth}/${endDay}/${endYear}`;
                      
                      return (
                        <div style={{ 
                          fontSize: '12px', 
                          marginTop: '8px',
                          padding: '6px 8px',
                          borderRadius: '4px',
                          background: '#d4edda',
                          color: '#155724',
                          fontWeight: '500'
                        }}>
                          <FiCalendar style={{ marginRight: '4px' }} />
                          {startDisplay} - {endDisplay} ({daysDifference} day{daysDifference !== 1 ? 's' : ''})
                        </div>
                      );
                    })()}
                  </FormSection>
                </div>

                <div>
                  <FormSection>
                    <Label>Shift Type *</Label>
                    <Select 
                      value={assignmentSet.shiftType} 
                      onChange={(e) => updateAssignmentSet(assignmentSet.id, { shiftType: e.target.value })}
                      required
                    >
                      <option value="day">Day Shift</option>
                      <option value="noon">Noon Shift</option>
                    </Select>
                  </FormSection>
                  

                </div>
              </FormContainer>
            </div>
          ))}

          <div style={{ marginBottom: '20px' }}>
            <Button 
              type="button" 
              onClick={addAssignmentSet}
              style={{ 
                backgroundColor: '#28a745',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                color: 'white',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              + Add Another Assignment Set
            </Button>
          </div>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <ButtonGroup>
            <Button type="button" className="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : `Create All Assignments`}
            </Button>
          </ButtonGroup>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default BulkAssignmentForm;